import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { cleanSummary } from "@/lib/cleanSummary";
import { ArrowLeft, Bookmark, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/shell/AppShell";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { NsdcBadge, WekitBadge } from "@/components/certifications/CertCard";
import { useAuth } from "@/contexts/AuthContext";
import { useCareerDna } from "@/hooks/useCareerDna";
import { loadCareersBundle } from "@/data/careersBundle";
import { loadCareerIntel } from "@/lib/careerIntel";
import {
  loadCertifications, loadCertLinks, loadMyCertStatus, resolveCareerIds, setMyCertStatus,
  type CertLink, type CertStatus, type Certification,
} from "@/lib/certifications";

type Job = { ciId: string; name: string; level: string; start: string | null; mid: string | null };

export default function CertificationDetail() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: dna } = useCareerDna();
  const [cert, setCert] = useState<Certification | null | undefined>(undefined);
  const [link, setLink] = useState<CertLink | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [myIds, setMyIds] = useState<string[]>([]);
  const [status, setStatus] = useState<CertStatus | null>(null);

  useEffect(() => {
    loadCertifications().then((all) => setCert(all.find((c) => c.id === id) ?? null)).catch(() => setCert(null));
    loadCertLinks([id]).then((m) => setLink(m[id] ?? null)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (user) loadMyCertStatus(user.id).then((m) => setStatus(m[id] ?? null));
  }, [user, id]);

  useEffect(() => {
    if (dna?.matches?.length) resolveCareerIds(dna.matches).then(setMyIds);
  }, [dna]);

  useEffect(() => {
    if (!cert) return;
    let live = true;
    (async () => {
      const bundle = await loadCareersBundle();
      const byId = new Map(bundle.rows.map((r) => [r.ciId, r]));
      const rows = cert.careers.map((c) => byId.get(c)).filter(Boolean).slice(0, 8);
      const out = await Promise.all(rows.map(async (r) => {
        const intel = await loadCareerIntel(r!.name).catch(() => null);
        return { ciId: r!.ciId, name: r!.name, level: r!.level, start: intel?.salaryStart ?? null, mid: intel?.salaryMid ?? null };
      }));
      if (live) setJobs(out);
    })();
    return () => { live = false; };
  }, [cert]);

  const myMatches = useMemo(() => (cert ? cert.careers.filter((c) => myIds.includes(c)) : []), [cert, myIds]);
  const recommended = myMatches.length > 0;
  const skills = useMemo(() => (cert ? Array.from(new Set([...cert.programs, ...cert.tools])) : []), [cert]);

  const goToPlan = { label: "Go to plan", onClick: () => navigate("/next-steps") };
  const remind = (msg: string) => toast(msg, { action: goToPlan });
  const update = async (next: CertStatus | null, silent = false) => {
    if (!user) { toast("Sign in to save this to your plan."); return; }
    const prev = status;
    const { error } = await setMyCertStatus(user.id, id, next);
    if (error) { toast.error("That didn't save. Please try again."); return; }
    setStatus(next);
    if (silent) return;
    toast.success(next === "completed" ? "Marked as completed — well done." : "Saved to your career plan.", {
      action: goToPlan,
      cancel: { label: "Undo", onClick: () => void update(prev, true) },
    });
  };

  if (cert === undefined) return <AppShell title="Certification"><p className="text-muted-foreground">Loading…</p></AppShell>;
  if (!cert) return (
    <AppShell title="Certification">
      <p className="text-muted-foreground">We couldn't find that certification.</p>
      <Link to="/certifications" className="mt-4 inline-flex min-h-11 items-center text-primary">Browse certifications</Link>
    </AppShell>
  );

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="glass-card space-y-3 rounded-3xl p-5 sm:p-6">
      <h2 className="font-display text-lg font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );

  return (
    <AppShell title="Certification">
      <PageHead title={`${cert.name} — ${cert.provider} | WeKIT`}
        description={`${cert.name} by ${cert.provider}: an NSDC-approved certification, the skills it covers and the careers it helps you reach.`}
        path={`/certifications/${cert.id}`} />
      <div className="space-y-5">
        <Link to="/certifications" className="inline-flex min-h-11 items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All certifications
        </Link>

        <header className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <NsdcBadge />
            {recommended && <WekitBadge />}
          </div>
          <h1 className="font-display text-3xl font-bold text-foreground sm:text-4xl">{cert.name}</h1>
          <p className="text-muted-foreground">
            Official certification by {cert.provider}
            {cert.level ? ` · ${cert.level} level` : ""}{cert.nsqf ? ` · NSQF level ${cert.nsqf}` : ""}{cert.duration ? ` · ${cert.duration}` : ""}
          </p>
          {recommended && (
            <p className="rounded-2xl bg-accent/10 p-4 text-sm text-foreground">
              We recommend this because it lines up with your Career DNA™ and leads towards{" "}
              {jobs.filter((j) => myMatches.includes(j.ciId)).map((j) => j.name).slice(0, 2).join(" and ") || "your matched careers"}.
              {cert.level === "Foundation" ? " It's a good first step from where you are now." : ""}
            </p>
          )}
        </header>

        <div className="flex flex-col gap-3 sm:flex-row">
          {link?.url ? (
            <Button asChild className="h-12 rounded-2xl">
              <a href={link.url} target="_blank" rel="noopener noreferrer">
                Enrol on the official {cert.provider} website <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
          ) : (
            <p className="inline-flex min-h-12 items-center text-sm text-muted-foreground">We're still confirming the official enrolment link.</p>
          )}
          <Button variant="secondary" className="h-12 rounded-2xl" onClick={() => (status ? remind("Already in your career plan.") : update("saved"))}>
            <Bookmark className="mr-2 h-4 w-4" aria-hidden="true" /> {status ? "In your plan" : "Save to career plan"}
          </Button>
          <Button variant="outline" className="h-12 rounded-2xl" onClick={() => (status === "completed" ? remind("You've already completed this one.") : update("completed"))}>
            <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" /> {status === "completed" ? "Completed" : "Mark as completed"}
          </Button>
        </div>

        <Section title="Learn more">
          {(() => {
            const blocks = cleanSummary(link?.summary, cert.name);
            const overview = blocks.filter((b) => !b.heading);
            const faqs = blocks.filter((b) => b.heading);
            return (
              <div className="space-y-5">
                {overview.map((b, i) => (
                  <p key={i} className="max-w-prose text-base leading-relaxed text-foreground/90">{b.text}</p>
                ))}
                {faqs.length > 0 && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {faqs.map((b) => (
                      <div key={b.heading} className="rounded-2xl border border-border/60 bg-muted/30 p-4">
                        <h3 className="font-display text-sm font-semibold text-foreground">{b.heading}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.text}</p>
                      </div>
                    ))}
                  </div>
                )}
                <dl className="space-y-4 border-t border-border/60 pt-4">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Learning {cert.tracks.length > 1 ? "tracks" : "track"}</dt>
                    <dd className="mt-2 flex flex-wrap gap-2">
                      {cert.tracks.map((tr) => <span key={tr} className="rounded-full bg-primary/10 px-3 py-1.5 text-sm text-foreground">{tr}</span>)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Helpful to know first</dt>
                    <dd className="mt-2 text-sm text-foreground">{cert.prereqs.length ? cert.prereqs.join(", ") : "No prior course needed"}</dd>
                  </div>
                  {cert.degrees.length > 0 && (
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Fits alongside</dt>
                      <dd className="mt-2 text-sm text-foreground">{cert.degrees.join(" · ")}</dd>
                    </div>
                  )}
                </dl>
                {link?.source_domain && (
                  <p className="inline-flex rounded-full bg-muted/40 px-3 py-1 text-xs text-muted-foreground">Details from {link.source_domain}</p>
                )}
              </div>
            );
          })()}
        </Section>

        <Section title="Skills you'll gain">
          <ul className="flex flex-wrap gap-2">
            {skills.map((s) => <li key={s} className="rounded-full bg-muted/50 px-3 py-1.5 text-sm text-foreground">{s}</li>)}
          </ul>
        </Section>

        <Section title="Career impact">
          <p className="text-sm text-muted-foreground">
            Employers in these roles look for the skills this certification proves. It won't open a door on its own, but it gives you something concrete to show.
          </p>
        </Section>

        <Section title="Jobs that ask for these skills — and what they pay">
          <ul className="divide-y divide-border">
            {jobs.map((j) => (
              <li key={j.ciId}>
                <Link to={`/careers/${j.ciId}`} className="flex min-h-12 flex-col gap-1 py-3 hover:text-primary sm:flex-row sm:items-center sm:justify-between">
                  <span className="font-medium text-foreground">
                    {j.name}{myMatches.includes(j.ciId) && <span className="ml-2 text-xs text-accent">Your match</span>}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {j.start || j.mid ? [j.start && `Starts ${j.start}`, j.mid && `grows to ${j.mid}`].filter(Boolean).join(" → ") : "Salary not yet available"}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </AppShell>
  );
}
